// useSearchDefaultFeed — the live default board shown when the search box is
// empty:
//
//   • order = top N by 24h volume (desc), fetched once per open, filtered to
//     ≥ $10k liquidity and non-stablecoin (TON-only is guaranteed upstream);
//   • every WS trade patches values and re-ranks now;
//   • an off-board trade requests one coalesced authoritative membership
//     snapshot, with no timer, polling loop, or stale-first frame.

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchTokens } from '../api/tokens'
import type { TokenSummary, TradeSummary } from '../api/types'
import { HydrationTradeBuffer } from '../lib/hydrationTradeBuffer'
import { LiveTradeGate, reconcileTokenSnapshot } from '../lib/liveFeedGate'
import {
  applyTradeToTokenSummariesNoReorder,
  applyValuationPatchToTokenSummaries,
  rankTokensByVolume,
  tradeMatchesPrimaryTokenRoute,
} from '../lib/liveTradeRows'
import {
  DEFAULT_SEARCH_MIN_LIQUIDITY_USD,
  isDefaultSearchToken,
  isStablecoinToken,
} from '../lib/searchDefault'
import { mergeTokenBirth, rememberEarliestTokenBirth } from '../lib/tokenAge'
import { isDiscoverProductToken } from './useDiscoverFeed'
import { useWsReseed } from './useWsReseed'
import { useWsSubscription } from './useWsSubscription'

// The API applies the $10k gate before this response limit. Ten extra rows
// absorb the finite curated stablecoin exclusions without shipping the old
// 4x candidate page to every Search open.
const FETCH_HEADROOM = 10

export interface UseSearchDefaultFeedResult {
  tokens: TokenSummary[]
  loading: boolean
  error: boolean
}

interface LaunchpadBirthWs {
  jetton_master: string
  deploy_ts?: string
}

export function useSearchDefaultFeed(count = 30, enabled = true): UseSearchDefaultFeedResult {
  const [tokens, setTokens] = useState<TokenSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  const seq = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const inFlightRef = useRef(false)
  const queuedReloadRef = useRef(false)
  const queuedTradeRef = useRef<TradeSummary | undefined>(undefined)
  const loadRef = useRef<(background?: boolean, afterTrade?: TradeSummary) => void>(() => undefined)
  const enabledRef = useRef(enabled)
  const tokensRef = useRef(tokens)
  const coveredTradeKeysRef = useRef(new Set<string>())
  const rememberedBirthsRef = useRef(new Map<string, string>())
  enabledRef.current = enabled
  tokensRef.current = tokens
  const hydrationTradesRef = useRef(new HydrationTradeBuffer())
  const gateRef = useRef<LiveTradeGate | null>(null)
  if (gateRef.current == null) gateRef.current = new LiveTradeGate()
  const gate = gateRef.current

  // Fetch + filter one authoritative membership. Background rebases commit the
  // complete new snapshot atomically while preserving any uncovered live tail.
  const load = useCallback(
    (background = false, afterTrade?: TradeSummary) => {
      if (background && inFlightRef.current) {
        queuedReloadRef.current = true
        if (afterTrade) queuedTradeRef.current = afterTrade
        return
      }
      const mine = ++seq.current
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      inFlightRef.current = true
      if (!background) {
        setLoading(true)
        setError(false)
        setHydrated(false)
      }
      fetchTokens(
        {
          limit: count + FETCH_HEADROOM,
          sort: 'volume_24h',
          window: '24h',
          minLiquidityUSD: DEFAULT_SEARCH_MIN_LIQUIDITY_USD,
          afterTrade,
        },
        { signal: controller.signal }
      )
        .then((r) => {
          if (mine !== seq.current) return
          const covered = new Set(r.market_included_pending ?? [])
          if (afterTrade && !covered.has(pendingTradeKey(afterTrade))) {
            throw new Error('authoritative Top snapshot did not acknowledge the requested trade')
          }
          coveredTradeKeysRef.current = covered
          for (const trade of hydrationTradesRef.current.values()) {
            if (covered.has(pendingTradeKey(trade))) hydrationTradesRef.current.delete(trade)
          }
          const eligible = patchRememberedTokenBirths(
            r.items.filter(isDiscoverProductToken).filter(isDefaultSearchToken).slice(0, count),
            rememberedBirthsRef.current
          )
          gate.observeTokenSnapshots(eligible)
          setTokens((prev) => {
            const next = background ? reconcileSearchRows(prev, eligible, gate) : eligible
            tokensRef.current = next
            return next
          })
          setHydrated(true)
          setError(false)
          setLoading(false)
        })
        .catch((e: unknown) => {
          if (mine !== seq.current) return
          if (e instanceof DOMException && e.name === 'AbortError') return
          if (!background) {
            hydrationTradesRef.current.clear()
            setError(true)
            setLoading(false)
          }
        })
        .finally(() => {
          if (abortRef.current !== controller) return
          abortRef.current = null
          inFlightRef.current = false
          if (!queuedReloadRef.current || !enabledRef.current) return
          queuedReloadRef.current = false
          const queuedTrade = queuedTradeRef.current
          queuedTradeRef.current = undefined
          loadRef.current(true, queuedTrade)
        })
    },
    [count, gate]
  )
  loadRef.current = load

  useEffect(() => {
    if (!enabled) {
      ++seq.current
      abortRef.current?.abort()
      abortRef.current = null
      inFlightRef.current = false
      queuedReloadRef.current = false
      queuedTradeRef.current = undefined
      hydrationTradesRef.current.clear()
      coveredTradeKeysRef.current.clear()
      tokensRef.current = []
      setTokens([])
      setHydrated(false)
      setError(false)
      setLoading(false)
      return
    }
    load()
    return () => abortRef.current?.abort()
  }, [enabled, load])

  useEffect(() => {
    if (tokens.length === 0) return
    gate.observeTokenSnapshots(tokens)
  }, [gate, tokens])

  useWsReseed(
    useCallback(() => {
      if (!enabled) return
      gate.reset()
      load(true, hydrationTradesRef.current.values().at(-1))
    }, [enabled, gate, load])
  )

  /*
   * Dedup + ordering gate shared with the Discover/Pulse merge discipline
   * (REALTIME-CONSISTENCY): a re-delivered trade must not double-count volume.
   */
  const applyTrade = useCallback(
    (trade: TradeSummary) => {
      // The default board permanently excludes the curated stablecoin masters.
      // A secondary stablecoin route can never enter or mutate this membership,
      // so it must not start a barrier-backed full-board rebase.
      if (isStablecoinToken(trade.base_token ?? '')) return
      if (coveredTradeKeysRef.current.has(pendingTradeKey(trade))) {
        // Stage-3 B1: the authoritative snapshot acked this trade's key while
        // it was still pending — native counters are in its aggregates, the
        // USD share is not (the backend skips USD for unvalued journal
        // trades). The response tape retaining the event UNVALUED is exact
        // proof of that gap: apply a one-shot USD-only patch. Anything else
        // (unvalued frame, valued tape, no tape entry) stays a duplicate.
        if (gate.claimUnvaluedCoverage(trade)) {
          const newest = gate.isNewestKnownFor(trade)
          setTokens((prev) => {
            const next = rankTokensByVolume(
              applyValuationPatchToTokenSummaries(prev, trade, newest, {
                exactPoolLiquidity: true,
              })
            )
            tokensRef.current = next
            return next
          })
        }
        return
      }
      const exactRoute = tokensRef.current.some((row) =>
        tradeMatchesPrimaryTokenRoute(row.jetton_master, row.primary_pool_addr, trade)
      )
      if (!exactRoute) {
        // A sibling pool of an already-rendered token does not contribute to
        // the canonical primary-route row. Ignore it instead of overwriting
        // primary price/mcap/sparkline/volume with another route's event.
        if (tokensRef.current.some((row) => row.jetton_master === trade.base_token)) return
        hydrationTradesRef.current.push(trade)
        load(true, trade)
        return
      }
      const admission = gate.admit(trade)
      if (!admission.fresh) {
        // Stage-3 B1: the valued upgrade of an already-applied pending frame.
        if (!admission.patch) return
        setTokens((prev) => {
          const next = rankTokensByVolume(
            applyValuationPatchToTokenSummaries(prev, trade, admission.newest, {
              exactPoolLiquidity: true,
            })
          )
          tokensRef.current = next
          return next
        })
        return
      }
      if (
        admission.newest &&
        typeof trade.liquidity_usd === 'number' &&
        Number.isFinite(trade.liquidity_usd) &&
        trade.liquidity_usd < DEFAULT_SEARCH_MIN_LIQUIDITY_USD
      ) {
        // Membership changed. Keep the existing frame intact until the exact
        // after-trade barrier returns, then commit the ranked replacement once.
        hydrationTradesRef.current.push(trade)
        load(true, trade)
        return
      }
      setTokens((prev) => {
        const next = rankTokensByVolume(
          applyTradeToTokenSummariesNoReorder(prev, trade, {
            countersOnly: !admission.newest,
            exactPoolLiquidity: true,
          })
        )
        tokensRef.current = next
        return next
      })
    },
    [gate, load]
  )

  useEffect(() => {
    if (!enabled || !hydrated || hydrationTradesRef.current.size === 0) return
    for (const trade of hydrationTradesRef.current.drain()) applyTrade(trade)
  }, [applyTrade, enabled, hydrated])

  useWsSubscription<TokenSummary | TradeSummary | LaunchpadBirthWs>(
    enabled ? 'discover' : null,
    (msg) => {
      if (msg.event === 'launchpad.deploy') {
        const birth = msg.data as LaunchpadBirthWs
        const birthTS = rememberEarliestTokenBirth(
          rememberedBirthsRef.current,
          birth.jetton_master,
          birth.deploy_ts
        )
        if (!birthTS) return
        setTokens((current) => {
          const next = patchTokenBirth(current, birth.jetton_master, birthTS)
          tokensRef.current = next
          return next
        })
        return
      }
      if (msg.event === 'token.discovered' || msg.event === 'token.metadata') {
        load(true, hydrationTradesRef.current.values().at(-1))
        return
      }
      if (msg.event !== 'trade') return
      const trade = msg.data as TradeSummary
      if (!hydrated) {
        hydrationTradesRef.current.push(trade)
        return
      }
      applyTrade(trade)
    }
  )

  return enabled
    ? { tokens: hydrated ? tokens : [], loading: loading || (!hydrated && !error), error }
    : { tokens: [], loading: false, error: false }
}

function pendingTradeKey(trade: TradeSummary): string {
  const pool = trade.pool_addr ?? ''
  const tx = trade.tx_hash
  const nanos = BigInt(Math.trunc(trade.ts)) * 1_000_000_000n
  return `${pool.length}:${pool}${nanos}:${tx.length}:${tx}`
}

function reconcileSearchRows(
  live: TokenSummary[],
  snapshot: TokenSummary[],
  gate: LiveTradeGate
): TokenSummary[] {
  if (live.length === 0) return snapshot
  return rankTokensByVolume(reconcileTokenSnapshot(live, snapshot, gate))
}

function patchTokenBirth(
  tokens: TokenSummary[],
  jettonMaster: string,
  birthTS: string
): TokenSummary[] {
  let changed = false
  const next = tokens.map((token) => {
    if (token.jetton_master !== jettonMaster) return token
    const merged = mergeTokenBirth(token, { birth_ts: birthTS })
    if (merged === token) return token
    changed = true
    return merged
  })
  return changed ? next : tokens
}

function patchRememberedTokenBirths(
  tokens: TokenSummary[],
  births: ReadonlyMap<string, string>
): TokenSummary[] {
  let next = tokens
  for (const [jettonMaster, birthTS] of births) {
    next = patchTokenBirth(next, jettonMaster, birthTS)
  }
  return next
}
